/**
 * 发货弹窗组件
 * 用于选择物流渠道、填写发货信息、预估运费
 */

import React, { useState, useEffect } from 'react';
import { Modal, ModalContainer, Overlay } from '../ModalContainer';
import Button from '../shared/Button';
import Input from '../Input';
import SelectOption from '../SelectOption';
import { getLogisticsProducts, shipOrder, estimateShippingCost } from '../../utils/Apis/shipping';

const ShipOrderModal = ({ isOpen, onClose, order, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [products, setProducts] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState('');
  const [estimatedCost, setEstimatedCost] = useState(null);

  const [formData, setFormData] = useState({
    sender_name: '',
    sender_phone: '',
    sender_address: '',
    sender_city: '',
    sender_country: 'CN',
    receiver_name: '',
    receiver_phone: '',
    receiver_address: '',
    receiver_city: '',
    receiver_country: '',
    receiver_zipcode: '',
    length: 10,
    width: 10,
    height: 10,
    weight: 0.5,
  });

  useEffect(() => {
    if (isOpen) {
      loadProducts();
      setEstimatedCost(null);
      setFormData(prev => ({
        ...prev,
        receiver_name: order?.shippingInfo?.contactName || order?.user?.name || '',
        receiver_phone: order?.shippingInfo?.phoneNo || '',
        receiver_address: order?.shippingInfo?.address || '',
        receiver_city: order?.shippingInfo?.city || '',
        receiver_country: order?.shippingInfo?.country || '',
        receiver_zipcode: order?.shippingInfo?.zipCode || '',
      }));
    }
  }, [isOpen, order]);

  const loadProducts = async () => {
    try {
      const result = await getLogisticsProducts();
      console.log('[ShipOrderModal] API 返回数据:', result);
      const productList = result.products || [];
      console.log('[ShipOrderModal] 解析后的产品列表:', productList);
      setProducts(productList);
      // 默认选中"全部（自动匹配）"
      setSelectedProduct('ALL');
    } catch (error) {
      console.error('Failed to load products', error);
    }
  };

  const handleEstimate = async () => {
    if (!selectedProduct || !formData.weight) return;
    try {
      const weightInGrams = String(Math.round(Number(formData.weight) * 1000));
      // 当选择"ALL"时，传递空字符串让4PX自动匹配
      const productCode = selectedProduct === 'ALL' ? '' : String(selectedProduct || '');
      const result = await estimateShippingCost({
        logistics_product_code: productCode,
        weight: weightInGrams,
        country_code: formData.receiver_country || order?.shippingInfo?.country || 'US',
        recipient_post_code: formData.receiver_zipcode || order?.shippingInfo?.zipCode || '',
        length: formData.length,
        width: formData.width,
        height: formData.height,
        cargocode: 'P',
      });
      console.log('[ShipOrderModal] 预估费用返回数据:', result);
      // 从嵌套结构中提取预估费用对象
      setEstimatedCost(result.estimate?.quotes?.[0] || result.estimate?.estimate || result.estimate?.raw?.costList?.[0] || null);
    } catch (error) {
      console.error('Failed to estimate cost', error);
      alert('预估费用失败: ' + (error.response?.data?.message || error.message || '未知错误'));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      // 当选择"ALL"时，传递空字符串让4PX自动匹配
      const productCode = selectedProduct === 'ALL' ? '' : String(selectedProduct || '');
      await shipOrder({
        orderId: order._id,
        logistics_product_code: productCode,
        shippingData: formData,
      });
      if (onSuccess) {
        onSuccess();
      }
      onClose();
    } catch (error) {
      const backendMessage = error.response?.data?.message;
      const backendCode = error.response?.data?.code;
      const errorText = backendCode ? `${backendMessage || error.message || '未知错误'} (${backendCode})` : (backendMessage || error.message || '未知错误');
      alert('发货失败: ' + errorText);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  if (!isOpen) return null;

  return (
    <ModalContainer>
      <Overlay onClick={onClose} />
      <Modal className="w-full max-w-3xl bg-background rounded-lg shadow-xl max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <h2 className="text-2xl font-bold mb-6 text-foreground">发货 - 4PX 全球直发</h2>
          <form onSubmit={handleSubmit}>
            <div className="mb-6">
              <div className="flex items-center gap-2 mb-3 pb-2 border-b border-gray-200 dark:border-gray-700">
                <span className="text-lg font-semibold text-foreground">物流渠道</span>
              </div>
              <SelectOption
                id="logistics_product"
                name="logistics_product"
                label="物流渠道"
                value={selectedProduct}
                onChange={(e) => setSelectedProduct(e.target.value)}
                options={[
                  // 添加"全部"选项，传递空数组让4PX自动匹配所有可用产品
                  { value: 'ALL', name: '全部（自动匹配）' },
                  ...products.map(p => ({
                    value: p.logistics_product_code || p.code,
                    name: p.logistics_product_name_cn || p.logistics_product_name || p.name || p.logistics_product_code || p.code
                  }))
                ]}
                required
              />
            </div>

            <div className="mb-6">
              <div className="flex items-center gap-2 mb-3 pb-2 border-b border-gray-200 dark:border-gray-700">
                <span className="text-lg font-semibold text-foreground">发货人信息</span>
              </div>
              <div className="space-y-4">
                <div className="space-y-4">
                  <Input
                    label="发货人姓名"
                    value={formData.sender_name}
                    onChange={(e) => handleInputChange('sender_name', e.target.value)}
                    required
                  />
                  <Input
                    label="发货人电话"
                    value={formData.sender_phone}
                    onChange={(e) => handleInputChange('sender_phone', e.target.value)}
                    required
                  />
                </div>
                <Input
                  label="发货人地址"
                  value={formData.sender_address}
                  onChange={(e) => handleInputChange('sender_address', e.target.value)}
                  required
                />
                <div className="space-y-4">
                  <Input
                    label="发货城市"
                    value={formData.sender_city}
                    onChange={(e) => handleInputChange('sender_city', e.target.value)}
                    required
                  />
                  <Input
                    label="发货国家"
                    value={formData.sender_country}
                    onChange={(e) => handleInputChange('sender_country', e.target.value)}
                    required
                  />
                </div>
              </div>
            </div>

            <div className="mb-6">
              <div className="flex items-center gap-2 mb-3 pb-2 border-b border-gray-200 dark:border-gray-700">
                <span className="text-lg font-semibold text-foreground">收货人信息</span>
              </div>
              <div className="space-y-4">
                <Input
                  label="收货人姓名"
                  value={formData.receiver_name}
                  onChange={(e) => handleInputChange('receiver_name', e.target.value)}
                  required
                />
                <Input
                  label="收货人电话"
                  value={formData.receiver_phone}
                  onChange={(e) => handleInputChange('receiver_phone', e.target.value)}
                  required
                />
                <Input
                  label="收货人地址"
                  value={formData.receiver_address}
                  onChange={(e) => handleInputChange('receiver_address', e.target.value)}
                  required
                />
                <div className="space-y-4">
                  <Input
                    label="收货城市"
                    value={formData.receiver_city}
                    onChange={(e) => handleInputChange('receiver_city', e.target.value)}
                    required
                  />
                  <Input
                    label="收货国家"
                    value={formData.receiver_country}
                    onChange={(e) => handleInputChange('receiver_country', e.target.value)}
                    required
                  />
                </div>
                <Input
                  label="收货邮编"
                  value={formData.receiver_zipcode}
                  onChange={(e) => handleInputChange('receiver_zipcode', e.target.value)}
                  required
                />
                <p className="text-sm text-muted-foreground">
                  这里修改仅用于本次发货请求，不会修改订单里的收货地址。
                </p>
              </div>
            </div>

            <div className="mb-6">
              <div className="flex items-center gap-2 mb-3 pb-2 border-b border-gray-200 dark:border-gray-700">
                <span className="text-lg font-semibold text-foreground">包裹信息</span>
              </div>
              <div className="space-y-4">
                <div className="space-y-4">
                  <Input
                    label="长度(cm)"
                    type="number"
                    value={formData.length}
                    onChange={(e) => handleInputChange('length', e.target.value)}
                  />
                  <Input
                    label="宽度(cm)"
                    type="number"
                    value={formData.width}
                    onChange={(e) => handleInputChange('width', e.target.value)}
                  />
                  <Input
                    label="高度(cm)"
                    type="number"
                    value={formData.height}
                    onChange={(e) => handleInputChange('height', e.target.value)}
                  />
                </div>
                <Input
                  label="重量(kg)"
                  type="number"
                  step="0.01"
                  value={formData.weight}
                  onChange={(e) => handleInputChange('weight', e.target.value)}
                  required
                />
              </div>
            </div>

            {estimatedCost && (
              <div className="bg-gray-50 dark:bg-gray-800 p-4 rounded-lg mb-6 border border-gray-200 dark:border-gray-700">
                <p className="text-gray-900 dark:text-gray-300 font-medium">
                  预估费用: <span className="text-xl font-bold">{estimatedCost.lumpSumFee || estimatedCost.total_fee || estimatedCost.totalFee || estimatedCost.estimated_cost || estimatedCost.estimatedCost || '0'}</span> {estimatedCost.currency || estimatedCost.currencyCode || 'USD'}
                </p>
                {estimatedCost.estimated_time && (
                  <p className="mt-2 text-sm text-gray-700 dark:text-gray-300">
                    预计时效: {estimatedCost.estimatedTime || estimatedCost.estimated_time}
                  </p>
                )}
                {estimatedCost.remarks && (
                  <p className="mt-1 text-sm text-gray-700 dark:text-gray-300">
                    备注: {estimatedCost.remark || estimatedCost.remarks}
                  </p>
                )}
                {/* 显示费用明细 */}
                    {estimatedCost.costList && estimatedCost.costList.length > 0 && (
                      <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-700">
                        <p className="text-sm font-semibold text-gray-900 dark:text-gray-300 mb-2">费用明细:</p>
                        {estimatedCost.costList.map((cost, index) => (
                          <div key={index} className="text-sm text-gray-700 dark:text-gray-300">
                            {cost.costName || cost.cost_name}: {cost.costValue || cost.cost_value} {cost.currency || 'USD'}
                          </div>
                        ))}
                      </div>
                    )}
                {/* 调试信息：显示原始数据 */}
                {process.env.NODE_ENV === 'development' && (
                  <details className="mt-3">
                    <summary className="cursor-pointer text-xs text-gray-600 dark:text-gray-400">调试信息</summary>
                    <pre className="mt-2 text-xs bg-gray-100 dark:bg-gray-900/50 p-2 rounded overflow-x-auto text-gray-400 dark:text-gray-500">
                      {JSON.stringify(estimatedCost, null, 2)}
                    </pre>
                  </details>
                )}
              </div>
            )}

            <div className="flex gap-3 justify-end pt-4 border-t border-gray-200 dark:border-gray-700">
              <Button type="button" onClick={onClose} variant="secondary">
                取消
              </Button>
              <Button type="button" onClick={handleEstimate} variant="outline">
                预估费用
              </Button>
              <Button type="submit" disabled={loading}>
                {loading ? '发货中...' : '确认发货'}
              </Button>
            </div>
          </form>
        </div>
      </Modal>
    </ModalContainer>
  );
};

export default ShipOrderModal;
